"use client";
import Image from "next/image";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import GoogleIcon from "./GoogleIcon";

/* Google's four-colour "G" mark for the badge */
function GoogleG() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#4285F4"
        d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
      />
      <path
        fill="#FBBC05"
        d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34A21.98 21.98 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"
      />
      <path
        fill="#EA4335"
        d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
      />
    </svg>
  );
}

export default function Hero() {
  return (
    <section
      className="relative w-full overflow-hidden"
      style={{
        backgroundColor: "#ffffff",
        backgroundImage: `
    radial-gradient(
      60% 80% at 50% -10%,
      rgba(127, 161, 255, 0.2) 0%,
      rgba(127, 161, 255, 0) 60%
    ),
    repeating-linear-gradient(
      to right,
      rgba(102, 102, 102, 0.13) 0 1px,
      transparent 1px 76px
    ),
    repeating-linear-gradient(
      to bottom,
      rgba(102, 102, 102, 0.13) 0 1px,
      transparent 1px 76px
    )
  `,

        backgroundRepeat: "no-repeat",
        backgroundPosition: "center -10px, 0 -10px, 0 -30px",
      }}
    >
      <div className="relative px-6 md:px-[40px] xl:px-20 pt-12 lg:pt-24 pb-16">
        {/* soft white spotlight behind the heading */}
        <div className="absolute top-3/4 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1] w-[1400px] h-[800px] mt-10 bg-white rounded-full blur-[107.63px]" />

        <div className="relative z-20 flex flex-col items-center text-center">
          {/* badge */}
          <div className="mb-8 mt-4 flex items-center gap-2 rounded-full border border-[#1414141A]/10 bg-white py-1.5 pl-1 pr-2">
            <GoogleG />
            <span className="text-[12px] font-medium text-[#292929]">
              Google Ads Platform
            </span>
          </div>

          <h1 className="w-full flex flex-col md:flex-row md:flex-wrap items-center md:items-baseline justify-center gap-x-3 sm:gap-1 md:gap-x-3 max-w-[820px] md:max-w-none solution-heading">
            <span>AI Copy Built</span>
            <span className="flex items-baseline gap-3">
              for
              <GoogleIcon />
            </span>
          </h1>

          <p className="mt-[18px] max-w-[300px] sm:max-w-[400px] md:max-w-[420px] lg:max-w-[560px] xl:max-w-[640px] 2xl:max-w-[1000px] body-secondary-text">
            Generate high-intent search and display ad copy that ranks and
            converts-headlines, descriptions, and sitelinks. Instantly.
          </p>

          {/* CTAs */}
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://app.imagive.ai?redirect=/users/white-chat-board"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary group gap-2 inline-flex items-center justify-center"
            >
              Generate Google Copy Now{" "}
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 duration-300 transition-all"
              />
            </a>
            <a
              href="https://app.imagive.ai?redirect=/users/white-chat-board"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary w-full sm:w-auto inline-flex items-center justify-center"
            >
              No Credit Card Required
            </a>
          </div>
        </div>
      </div>

      <div className="flex justify-center items-center -space-x-8 mt-14 lg:mt-28">
        <motion.div
          initial={{
            y: 80,
            rotate: -25,
            opacity: 1,
          }}
          animate={{
            y: 0,
            rotate: -15,
            opacity: 1,
          }}
          transition={{
            duration: 0.8,
            delay: 0.2,
          }}
          className="relative z-10  "
        >
          <Image
            src="/assets/platforms/google/1.svg"
            alt="fanned ads"
            width={220}
            height={260}
            className="h-auto"
            priority
          />
        </motion.div>

        <motion.div
          initial={{
            y: 80,
            rotate: 15,
            opacity: 1,
          }}
          animate={{
            y: 0,
            rotate: 6,
            opacity: 1,
          }}
          transition={{
            duration: 0.8,
            delay: 0.2,
          }}
          className="relative z-10  "
        >
          <Image
            src="/assets/platforms/google/2.svg"
            alt="fanned ads"
            width={220}
            height={260}
            className="h-auto"
            priority
          />
        </motion.div>

        <motion.div
          initial={{
            y: 80,
            rotate: -15,
            opacity: 1,
          }}
          animate={{
            y: 0,
            opacity: 1,
            rotate: -8,
          }}
          transition={{
            duration: 0.8,
            delay: 0.2,
          }}
          className="relative z-10  "
        >
          <Image
            src="/assets/platforms/google/3.svg"
            alt="fanned ads"
            width={220}
            height={260}
            className="h-auto"
            priority
          />
        </motion.div>

        <motion.div
          initial={{
            y: 80,
            rotate: 10,
            opacity: 1,
          }}
          animate={{
            y: 0,
            rotate: 15,
            opacity: 1,
          }}
          transition={{
            duration: 0.8,
            delay: 0.2,
          }}
          className="relative z-10 "
        >
          <Image
            src="/assets/platforms/google/4.svg"
            alt="fanned ads"
            width={220}
            height={260}
            className="h-auto"
            priority
          />
        </motion.div>
      </div>

      {/* trusted strip */}
      <div className="relative z-30 bg-white border-t border-b border-[#F0EEEC] lg:-mt-12">
        <div className="px-6 md:px-[40px] xl:px-20 py-8 lg:py-6">
          <p className="text-center text-[18px] md:text-[22px] font-semibold text-[#1C1917]">
            Trusted by high-growth performance teams and agencies worldwide
          </p>
        </div>
      </div>
    </section>
  );
}
